import { Box, Grid } from '@mui/material'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { RootState, useTypedDispatch, useTypedSelector } from '../..'
import { fetchDefectsPageAllowedAction } from '../../actions/user'
import Page from '../../components/page/Page'
import SearchToolCard from '../../components/search/SearchToolCard'
import Compositions from '../../icons/Compositions'
import Defect from '../../icons/Defect'
import Download from '../../icons/Download'
import Personnel from '../../icons/Personnel'
import Phone from '../../icons/Phone'
import ServiceOrder from '../../icons/ServiceOrders'
import Shift from '../../icons/Shift'
import Stopwatch from '../../icons/Stopwatch'
import Towing from '../../icons/Towing'
import {
  AllowedSelectorWrapper,
  allowTimetableFetchSelector,
  createRoleSelector,
} from '../../lib/roleUtils'

type SearchToolConfiguration = Record<
  string,
  { icon: JSX.Element; link: string; allowedSelector?: (state: RootState) => boolean }
>

const searchToolConfiguration: SearchToolConfiguration = {
  personnel: {
    icon: <Personnel iconSize="extraHuge" />,
    link: '/search/personnel',
    allowedSelector: () => true,
  },
  compositions: {
    icon: <Compositions iconSize="colossal" />,
    link: '/search/compositions',
    allowedSelector: createRoleSelector(
      'driver',
      'conductor',
      'waiter',
      'logistics_driver',
      'maintenance',
      'other'
    ),
  },
  defects: {
    icon: <Defect iconSize="extraHuge" />,
    link: '/search/defects',
    allowedSelector: (state) => !!state.user.defectsPageAllowed,
  },
  shifts: {
    icon: <Shift iconSize="extraHuge" />,
    link: '/search/shifts',
    allowedSelector: createRoleSelector(
      'driver',
      'conductor',
      'waiter',
      'logistics_driver',
      'commuter_manager'
    ),
  },
  download: {
    icon: <Download iconSize="extraHuge" />,
    link: '/search/fallbackTimetables',
    allowedSelector: allowTimetableFetchSelector,
  },
  phone: {
    icon: <Phone iconSize="extraHuge" />,
    link: '/search/contacts',
    allowedSelector: () => true,
  },
  stopwatch: {
    icon: <Stopwatch iconSize="extraHuge" width="38" />,
    link: '/search/punctuality',
    allowedSelector: (state) => !state.user.commuter_security,
  },
  towing: {
    icon: <Towing iconSize="gigantic" />,
    link: '/search/towings',
    allowedSelector: createRoleSelector(
      'driver',
      'logistics_driver',
      'commuter_driver',
      'maintenance',
      'other'
    ),
  },
  serviceOrders: {
    icon: <ServiceOrder iconSize="extraHuge" />,
    link: '/search/serviceOrders',
    allowedSelector: (state) => state.user.serviceDriver || state.user.actAsServiceDriver,
  },
}

const SearchToolPage = (): JSX.Element => {
  const { t } = useTranslation()
  const dispatch = useTypedDispatch()

  const defectsPageAllowed = useTypedSelector((state) => state.user.defectsPageAllowed)

  useEffect(() => {
    if (defectsPageAllowed === undefined) {
      dispatch(fetchDefectsPageAllowedAction())
    }
  }, [defectsPageAllowed, dispatch])

  return (
    <Page menu="search" overflowVisible>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          paddingTop: 3,
          paddingBottom: 4.5,
          margin: '0 auto',
          maxWidth: '382px',
        }}
      >
        <Grid container sx={{ gap: 3, justifyContent: 'center' }}>
          {Object.keys(searchToolConfiguration).map((key) => (
            <AllowedSelectorWrapper
              key={key}
              selector={searchToolConfiguration[key].allowedSelector}
            >
              <SearchToolCard
                title={t(`searchTool.title.${key}`)}
                icon={searchToolConfiguration[key].icon}
                link={searchToolConfiguration[key].link}
              />
            </AllowedSelectorWrapper>
          ))}
        </Grid>
      </Box>
    </Page>
  )
}

export default SearchToolPage
